import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ProjectOut } from "@/lib/api";

const STORAGE_KEY = "tm_active_project_id";

type Ctx = {
  activeId: number | null;
  setActiveId: (id: number | null) => void;
  resolve: (projects: ProjectOut[] | undefined) => ProjectOut | null;
};

const ActiveProjectCtx = createContext<Ctx | null>(null);

export function ActiveProjectProvider({ children }: { children: ReactNode }) {
  const [activeId, setActiveIdState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });

  function setActiveId(id: number | null) {
    setActiveIdState(id);
    if (typeof window !== "undefined") {
      if (id == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(id));
    }
  }

  const value = useMemo<Ctx>(
    () => ({
      activeId,
      setActiveId,
      resolve(projects) {
        if (!projects || projects.length === 0) return null;
        return projects.find((p) => p.id === activeId) ?? projects[0];
      },
    }),
    [activeId],
  );

  return <ActiveProjectCtx.Provider value={value}>{children}</ActiveProjectCtx.Provider>;
}

export function useActiveProject() {
  const ctx = useContext(ActiveProjectCtx);
  if (!ctx) throw new Error("useActiveProject must be used inside <ActiveProjectProvider>");
  return ctx;
}

export function useEnsureActiveProject(projects: ProjectOut[] | undefined) {
  const { activeId, setActiveId } = useActiveProject();
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    if (activeId == null || !projects.some((p) => p.id === activeId)) {
      setActiveId(projects[0].id);
    }
  }, [projects, activeId, setActiveId]);
}
